import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Package, DollarSign, User, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';

const LineItemEditModal = ({ 
  isOpen, 
  onClose, 
  lineItem, 
  onSave, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    quantity: '',
    salesPrice: '',
    salesDate: '',
    productNdc: '',
    productName: '',
    genericName: '',
    brandName: '',
    strength: '',
    dosageForm: '',
    packageSize: '',
    unitOfMeasure: '',
    listPrice: '',
    rebatePrice: '',
    chargebackPrice: '',
    netPrice: '',
    discountAmount: '',
    rebateAmount: '',
    contractPrice: '',
    validatedPrice: '',
    variance: '',
    tolerance: '',
    isWithinTolerance: false,
    ediLineNumber: '',
    ediProductCode: '',
    ediUnitOfMeasure: '',
    ediQuantity: '',
    ediPrice: '',
    notes: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (lineItem) {
      setFormData({
        quantity: lineItem.quantity || '',
        salesPrice: lineItem.salesPrice || '',
        salesDate: lineItem.salesDate ? new Date(lineItem.salesDate).toISOString().split('T')[0] : '',
        productNdc: lineItem.productNdc || '',
        productName: lineItem.productName || '',
        genericName: lineItem.genericName || '',
        brandName: lineItem.brandName || '',
        strength: lineItem.strength || '',
        dosageForm: lineItem.dosageForm || '',
        packageSize: lineItem.packageSize || '',
        unitOfMeasure: lineItem.unitOfMeasure || '',
        listPrice: lineItem.listPrice || '',
        rebatePrice: lineItem.rebatePrice || '',
        chargebackPrice: lineItem.chargebackPrice || '',
        netPrice: lineItem.netPrice || '',
        discountAmount: lineItem.discountAmount || '',
        rebateAmount: lineItem.rebateAmount || '',
        contractPrice: lineItem.contractPrice || '',
        validatedPrice: lineItem.validatedPrice || '',
        variance: lineItem.variance || '',
        tolerance: lineItem.tolerance || '',
        isWithinTolerance: lineItem.isWithinTolerance || false,
        ediLineNumber: lineItem.ediLineNumber || '',
        ediProductCode: lineItem.ediProductCode || '',
        ediUnitOfMeasure: lineItem.ediUnitOfMeasure || '',
        ediQuantity: lineItem.ediQuantity || '',
        ediPrice: lineItem.ediPrice || '',
        notes: lineItem.notes || ''
      });
      setValidationErrors({});
    }
  }, [lineItem]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.quantity || formData.quantity <= 0) {
      errors.quantity = 'Quantity must be greater than 0';
    }
    
    if (!formData.salesPrice || formData.salesPrice <= 0) {
      errors.salesPrice = 'Sales price must be greater than 0';
    }
    
    if (!formData.salesDate) {
      errors.salesDate = 'Sales date is required';
    }
    
    if (!formData.productNdc) {
      errors.productNdc = 'Product NDC is required';
    }
    
    if (!formData.productName) {
      errors.productName = 'Product name is required';
    }

    // Validate numeric fields
    const numericFields = ['listPrice', 'rebatePrice', 'chargebackPrice', 'netPrice', 'discountAmount', 'rebateAmount', 'ediPrice', 'contractPrice', 'validatedPrice', 'variance', 'tolerance'];
    numericFields.forEach(field => {
      if (formData[field] && (isNaN(formData[field]) || parseFloat(formData[field]) < 0)) {
        errors[field] = `${field.replace(/([A-Z])/g, ' $1').toLowerCase()} must be a valid positive number`;
      }
    });

    // Validate package size
    if (formData.packageSize && (isNaN(formData.packageSize) || parseFloat(formData.packageSize) <= 0)) {
      errors.packageSize = 'Package size must be a valid positive number';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    const updatedLineItem = {
      ...lineItem,
      ...formData,
      quantity: parseFloat(formData.quantity),
      salesPrice: parseFloat(formData.salesPrice),
      salesDate: new Date(formData.salesDate),
      totalAmount: parseFloat(formData.quantity) * parseFloat(formData.salesPrice)
    };

    onSave(updatedLineItem);
  };

  if (!isOpen || !lineItem) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Line Item</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Product Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Product Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name
                </label>
                <input
                  type="text"
                  name="productName"
                  value={formData.productName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.productName ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.productName && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.productName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product NDC
                </label>
                <input
                  type="text"
                  name="productNdc"
                  value={formData.productNdc}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.productNdc ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validationErrors.productNdc && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.productNdc}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Generic Name
                </label>
                <input
                  type="text"
                  name="genericName"
                  value={formData.genericName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Name
                </label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Strength
                </label>
                <input
                  type="text"
                  name="strength"
                  value={formData.strength}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dosage Form
                </label>
                <select
                  name="dosageForm"
                  value={formData.dosageForm}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Dosage Form</option>
                  <option value="TABLET">Tablet</option>
                  <option value="CAPSULE">Capsule</option>
                  <option value="INJECTION">Injection</option>
                  <option value="OPHTHALMIC">Ophthalmic</option>
                  <option value="TOPICAL">Topical</option>
                  <option value="ORAL">Oral</option>
                  <option value="INHALATION">Inhalation</option>
                  <option value="PATCH">Patch</option>
                  <option value="CREAM">Cream</option>
                  <option value="OINTMENT">Ointment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Package Size
                </label>
                <input
                  type="number"
                  name="packageSize"
                  value={formData.packageSize}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.packageSize ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="1"
                />
                {validationErrors.packageSize && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.packageSize}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit of Measure
                </label>
                <select
                  name="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Unit</option>
                  <option value="EA">Each</option>
                  <option value="ML">Milliliter</option>
                  <option value="MG">Milligram</option>
                  <option value="GM">Gram</option>
                  <option value="KG">Kilogram</option>
                  <option value="L">Liter</option>
                  <option value="OZ">Ounce</option>
                  <option value="LB">Pound</option>
                  <option value="TAB">Tablet</option>
                  <option value="CAP">Capsule</option>
                </select>
              </div>
            </div>
          </div>

          {/* Line Item Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Line Item Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Line Number
                </label>
                <input
                  type="text"
                  value={lineItem.lineNo}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity *
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.quantity ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="1"
                />
                {validationErrors.quantity && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.quantity}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sales Price *
                </label>
                <input
                  type="number"
                  name="salesPrice"
                  value={formData.salesPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.salesPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.salesPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.salesPrice}</p>
                )}
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sales Date *
              </label>
              <input
                type="date"
                name="salesDate"
                value={formData.salesDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors.salesDate ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {validationErrors.salesDate && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.salesDate}</p>
              )}
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Amount (Calculated)
              </label>
              <input
                type="text"
                value={`$${(parseFloat(formData.quantity) * parseFloat(formData.salesPrice)).toFixed(2)}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                disabled
              />
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              Pricing Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Price
                </label>
                <input
                  type="number"
                  name="listPrice"
                  value={formData.listPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.listPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.listPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.listPrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rebate Price
                </label>
                <input
                  type="number"
                  name="rebatePrice"
                  value={formData.rebatePrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.rebatePrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.rebatePrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.rebatePrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chargeback Price
                </label>
                <input
                  type="number"
                  name="chargebackPrice"
                  value={formData.chargebackPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.chargebackPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.chargebackPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.chargebackPrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Net Price
                </label>
                <input
                  type="number"
                  name="netPrice"
                  value={formData.netPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.netPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.netPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.netPrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Amount
                </label>
                <input
                  type="number"
                  name="discountAmount"
                  value={formData.discountAmount}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.discountAmount ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.discountAmount && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.discountAmount}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rebate Amount
                </label>
                <input
                  type="number"
                  name="rebateAmount"
                  value={formData.rebateAmount}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.rebateAmount ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.rebateAmount && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.rebateAmount}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contract Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Contract Information
            </h3>
            
            {/* Customer & Contract Details (Read-only) */}
            <div className="mb-4 space-y-3">
              {/* Customer Information */}
              {lineItem?.claim?.customer && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-800">Customer Information</h4>
                    <button
                      onClick={() => window.open(`/customers/${lineItem.claim.customer.id}`, '_blank')}
                      className="text-green-600 hover:text-green-800 underline text-xs"
                      title="View customer"
                    >
                      View Customer →
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Customer Name:</span>
                      <span className="ml-2 font-medium text-gray-800">{lineItem.claim.customer.name}</span>
                    </div>
                    {lineItem.claim.customer.ediEntityId && (
                      <div>
                        <span className="text-gray-600">EDI Entity ID:</span>
                        <span className="ml-2 text-gray-800">{lineItem.claim.customer.ediEntityId}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contract Information */}
              {lineItem?.claim?.contract && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-800">Contract Information</h4>
                    <button
                      onClick={() => window.open(`/contracts/${lineItem.claim.contract.id}`, '_blank')}
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      title="View contract"
                    >
                      View Contract →
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Contract Number:</span>
                      <span className="ml-2 font-medium text-gray-800">{lineItem.claim.contract.number}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Description:</span>
                      <span className="ml-2 text-gray-800">{lineItem.claim.contract.description}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Price
                </label>
                <input
                  type="number"
                  name="contractPrice"
                  value={formData.contractPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.contractPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.contractPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.contractPrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Validated Price
                </label>
                <input
                  type="number"
                  name="validatedPrice"
                  value={formData.validatedPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.validatedPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.validatedPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.validatedPrice}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Variance (%)
                </label>
                <input
                  type="number"
                  name="variance"
                  value={formData.variance}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.variance ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.variance && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.variance}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tolerance (%)
                </label>
                <input
                  type="number"
                  name="tolerance"
                  value={formData.tolerance}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.tolerance ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.tolerance && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.tolerance}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isWithinTolerance"
                    checked={formData.isWithinTolerance}
                    onChange={(e) => setFormData(prev => ({ ...prev, isWithinTolerance: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Within Tolerance</span>
                </label>
              </div>
            </div>
          </div>

          {/* EDI Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              EDI Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EDI Line Number
                </label>
                <input
                  type="text"
                  name="ediLineNumber"
                  value={formData.ediLineNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EDI Product Code
                </label>
                <input
                  type="text"
                  name="ediProductCode"
                  value={formData.ediProductCode}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EDI Unit of Measure
                </label>
                <select
                  name="ediUnitOfMeasure"
                  value={formData.ediUnitOfMeasure}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select EDI Unit</option>
                  <option value="EA">Each</option>
                  <option value="ML">Milliliter</option>
                  <option value="MG">Milligram</option>
                  <option value="GM">Gram</option>
                  <option value="KG">Kilogram</option>
                  <option value="L">Liter</option>
                  <option value="OZ">Ounce</option>
                  <option value="LB">Pound</option>
                  <option value="TAB">Tablet</option>
                  <option value="CAP">Capsule</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EDI Quantity
                </label>
                <input
                  type="number"
                  name="ediQuantity"
                  value={formData.ediQuantity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EDI Price
                </label>
                <input
                  type="number"
                  name="ediPrice"
                  value={formData.ediPrice}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    validationErrors.ediPrice ? 'border-red-300' : 'border-gray-300'
                  }`}
                  min="0"
                  step="0.01"
                />
                {validationErrors.ediPrice && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.ediPrice}</p>
                )}
              </div>
            </div>
          </div>

          {/* Claim Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Claim Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Claim Number
                </label>
                <input
                  type="text"
                  value={lineItem.claim?.claimNumber || 'N/A'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer
                </label>
                <input
                  type="text"
                  value={lineItem.claim?.customer?.name || 'N/A'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Validation Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              Validation Status
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Current Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  lineItem.validationStatus === 'VALIDATED' ? 'bg-green-100 text-green-800' :
                  lineItem.validationStatus === 'PENDING' ? 'bg-gray-100 text-gray-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {lineItem.validationStatus}
                </span>
              </div>
              {lineItem.validationErrors && lineItem.validationErrors.length > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Validation Errors:</span>
                  <ul className="mt-1 text-sm text-red-600 list-disc list-inside">
                    {lineItem.validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Add any notes about this line item..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            title="Save changes"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LineItemEditModal;
